import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  capitalGlobalReferenceDataService,
  type CapitalGlobalReferenceDataItem,
} from '@/services';

export function resolveBusinessSegmentDescription(
  segmentCode: string,
  items: CapitalGlobalReferenceDataItem[]
): string | undefined {
  const trimmed = segmentCode.trim();
  if (!trimmed) return undefined;

  const exactCodeMatch = items.find(item => item.code === trimmed);
  if (exactCodeMatch?.description?.trim()) {
    return exactCodeMatch.description.trim();
  }

  const exactDescriptionMatch = items.find(
    item => item.description?.trim() === trimmed
  );
  if (exactDescriptionMatch) {
    return trimmed;
  }

  return items[0]?.description?.trim() || undefined;
}

export function useCapitalGlobalBusinessSegmentLabel(segmentCode?: string) {
  const trimmed = segmentCode?.trim() ?? '';

  const query = useQuery({
    queryKey: [
      'capital-global-reference-data',
      'BusinessSegment',
      'by-code',
      trimmed,
    ],
    queryFn: async () => {
      const result = await capitalGlobalReferenceDataService.getReferenceData({
        tableName: 'BusinessSegment',
        filter: trimmed,
        pageNumber: 1,
        pageSize: 1000,
      });

      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Falha ao buscar segmento');
      }

      return result.data.data;
    },
    enabled: Boolean(trimmed),
    staleTime: 5 * 60 * 1000,
  });

  const label = useMemo(
    () => resolveBusinessSegmentDescription(trimmed, query.data ?? []),
    [query.data, trimmed]
  );

  return {
    label,
    isLoading: Boolean(trimmed) && query.isPending,
    isError: query.isError,
  };
}
